import { useEffect, type FC } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';

import { Button, Stack } from '@packages/shared';
import { Gallery32, List32 } from '@packages/themes/icons';
import { useMouseflow } from '@packages/tracking';
import { usePaging } from '@packages/search/src/hooks/usePaging';

import type { MobileDisplayMode, PagingMode } from '../../types';

export type DisplaySettingsMobileProps = {
  /** Currently selected display mode for the product grid
   * @default 'cards'
   */
  displayMode?: MobileDisplayMode;
  /** Callback fired when the display mode for the product grid changes */
  onChangeDisplayMode?: (displayMode: MobileDisplayMode) => void;
  /** Currently selected page size for the product grid
   * @default 'limited'
   * */
  pagingMode?: PagingMode;
};

const messages = defineMessages({
  gallery: {
    id: 'search.productList.displaySettings.displayModes.gallery',
    defaultMessage: 'Galerieansicht',
  },
  list: {
    id: 'search.productList.displaySettings.displayModes.list',
    defaultMessage: 'Listenansicht',
  },
});

/**
 * Component to allow the user to change the display settings for the product list
 * */
export const DisplaySettingsMobile: FC<DisplaySettingsMobileProps> = ({
  displayMode = 'cards',
  pagingMode = 'limited',
  onChangeDisplayMode,
}) => {
  const pushMouseflowData = useMouseflow();
  const { pushPageChange, pushPagingMode } = usePaging();

  useEffect(() => {
    if (pagingMode === 'infinite') {
      pushPageChange(1, true);
      pushPagingMode('limited');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Stack direction="row" width={1}>
      <Button
        startIcon={<Gallery32 sx={displayMode !== 'cards' ? { color: 'grey.main' } : undefined} />}
        variant="text"
        sx={{ flex: 1 }}
        onClick={() => {
          onChangeDisplayMode?.('cards');
          pushMouseflowData('search_ViewChangeSmallPictures');
        }}
      >
        <FormattedMessage {...messages.gallery} />
      </Button>
      <Button
        startIcon={
          <List32 sx={displayMode !== 'moreInformation' ? { color: 'grey.main' } : undefined} />
        }
        variant="text"
        sx={{ flex: 1 }}
        onClick={() => {
          onChangeDisplayMode?.('moreInformation');
          pushMouseflowData('search_ViewChangeMoreInformation');
        }}
      >
        <FormattedMessage {...messages.list} />
      </Button>
    </Stack>
  );
};
